/** A utility for constructing URL paths for requests sent to the OBX server. */
class ApiRequestPaths {
  /** Returns the endpoint to report a critical client error. */
  static errorReport = "Legacy/V0/ErrorReport";

  /** Returns the endpoint to get the calendar event url for a given appointment.
   * @param contactId - The id of the session's contact.
   * @param patientId - The id of the patient who will be attending the appointment.
   * @param appointmentId - The id of the appointment to obtain the calendar event url for.
   */
  static getCalendarEventUrl = (contactId: string, patientId: string, appointmentId: string) =>
    `Legacy/V0/Contacts/${contactId}/Customers/${patientId}/Appointments/${appointmentId}/CalendarEvent`;

  /** Returns the endpoint path to get the centers available for booking the current session.
   * @param postalCode - The postal code of the origin for the center lookup.
   * @param centerId - The id of the center to which the patient is assigned.
   */
  static getCentersV3 = (postalCode: string, centerId: string) =>
    `Legacy/V3/Centers?postalCode=${postalCode}&centerId=${centerId}`;

  /** Returns the endpoint path to get the experiments that are active for the current session. */
  static getActiveExperiments = `Legacy/V2/Experiments`;

  /** Returns the endpoint path to get the services available for booking at a given center. */
  static getCenterServices = (centerId: string) => `Legacy/V2/Centers/${centerId}/Services`;

  /** Returns the endpoint path to create a new patient note. */
  static postPatientNote = (contactId: string, patientId: string) =>
    `Legacy/V0/Contacts/${contactId}/Customers/${patientId}/Notes`;

  /** Returns the endpoint path to update an existing patient note. */
  static putPatientNote = (contactId: string, patientId: string, noteId: string) =>
    `Legacy/V0/Contacts/${contactId}/Customers/${patientId}/Notes/${noteId}`;

  /** Returns the endpoint path to report the session's device information. */
  static postDeviceInfo = (contactId: string) => `Legacy/V0/Contacts/${contactId}/Devices`;

  /** Returns the endpoint path to create the patient experiment assignment.
   * @param contactId - The id of the session's contact.
   * @param patientId - The id of the patient to be assigned to the experiment.
   * @param experimentId - The id of the experiment to assign the patient to.
   */
  static postPatientExperiment = (contactId: string, patientId: string, experimentId: string) =>
    `Legacy/V2/Contacts/${contactId}/Customers/${patientId}/Experiments/${experimentId}`;

  /** Returns the endpoint path to get the calendar for a center.
   * @param contactId - The id of the session's contact.
   * @param patientId - The id of the patient who will be booking with the calendar.
   * @param centerId - The id of the center to which the retrieved calendar belongs.
   */
  static getCenterCalendar = (contactId: string, patientId: string, centerId: string) =>
    `Legacy/V2/Ops/Contacts/${contactId}/Customers/${patientId}/Centers/${centerId}/Calendars`;

  /** Returns the endpoint path to confirm a reserved appointment.
   * @param contactId - The id of the sessions contact.
   * @param patientId - The id of the patient who will be making the reservation.
   * @param centerId - The id of the center where the reservation will be made.
   * @param slotId - The id of the slot which will be reserved.
   */
  static postAppointmentUrlV1_1 = (contactId: string, patientId: string, centerId: string, slotId: number) =>
    `Legacy/V1_1/Ops/Contacts/${contactId}/Customers/${patientId}/Centers/${centerId}/Appointments/Confirm/${slotId}`;

  /** Returns the endpoint path to create a reservation.
   * @param contactId - The id of the sessions contact.
   * @param patientId - The id of the patient who will be making the reservation.
   * @param centerId - The id of the center where the reservation will be made.
   * @param serviceId - The id of the service which will be conducted at the reserved appointment.
   * @param slotId - The id of the slot which will be reserved.
   */
  static postReservationUrlV1_1 = (
    contactId: string,
    patientId: string,
    centerId: string,
    serviceId: string,
    slotId: number
  ) =>
    `Legacy/V1_1/Ops/Contacts/${contactId}/Customers/${patientId}/Centers/${centerId}/Services/${serviceId}/Slots/${slotId}`;

  /** Returns the endpoint path to update the session's patient.
   * @param contactId - The id of the session's contact.
   * @param patientId - The id of the patient to be updated.
   */
  static putPatientUrl = (contactId: string, patientId: string) =>
    `Legacy/V0/Ops/Contacts/${contactId}/Customers/${patientId}`;

  /** Returns the endpoint path to post the patient's qualification results.
   * @param contactId - The id of the session's contact.
   * @param patientId - The id of the patient being qualified.
   */
  static postPatientQualification = (contactId: string, patientId: string) =>
    `Legacy/V0/Contacts/${contactId}/Customers/${patientId}/Qualifications`;

  /** Returns the endpoint path to update the session's contact result.
   * @param contactId - The id of the session's contact.
   */
  static postContactResult = (contactId: string) => `Legacy/V0/Contacts/${contactId}/Result`;

  /** The endpoint path used to obtain the data required for the session to start. */
  static postSessionStart = "Legacy/V2/SessionStart";

  /** Returns an endpoint to update the current step progress for the session.
   * @param contactId - The id of the session's contact.
   * @param flowId - The id of the session's flow.
   */
  static postStepProgress = (contactId: string, flowId: string) =>
    `Legacy/V0/Contacts/${contactId}/Flows/${flowId}/steps`;

  /** Returns the endpoint path to be used when updating the auth token. */
  static putToken = `Legacy/V0/Token`;

  /** Returns the endpoint path to get the disqualifying reasons for a contact.
   * @param contactId - The id of the session's contact.
   */
  static getDisqualifyingReasons = (contactId: string) => `Legacy/V0/Contacts/${contactId}/DisqualifyingReasons`;
}

export default ApiRequestPaths;
